import { CSSObject } from "@emotion/react";
import defaultAvatarUrl from "../assets/images/DefaultAvatar.png";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { TFunction } from "i18next";
import { GroupBase, StylesConfig } from "react-select";
import {
  IDateRangePreset,
  UserRole,
  TCurrency,
  StatusOptionData,
} from "../interfaces";
import { PlatformConfig } from "../features/Configurations/Integration/interface";

export const SIDEBAR = {
  EXPANDED_WIDTH: "270px",
  COLLAPSED_WIDTH: "80px",
} as const;

// Exchange rates relative to USD (approximate rates)
export const EXCHANGE_RATES: Record<string, number> = {
  AED: 3.67, AFN: 70.5, ALL: 94.2, AMD: 388, ANG: 1.79, AOA: 830, ARS: 825, AUD: 1.52, AWG: 1.79, AZN: 1.7,
  BAM: 1.79, BBD: 2, BDT: 110, BGN: 1.79, BHD: 0.37, BIF: 2850, BMD: 1, BND: 1.34, BOB: 6.9, BRL: 4.95,
  BSD: 1, BTN: 83, BWP: 13.5, BYN: 3.25, BZD: 2, CAD: 1.35, CDF: 2750, CHF: 0.88, CLP: 940, CNY: 7.2,
  COP: 3950, CRC: 512, CUP: 24, CVE: 101, CZK: 23.4, DJF: 177, DKK: 6.85, DOP: 58.5, DZD: 134, EGP: 30.9,
  ERN: 15, ETB: 56, EUR: 0.92, FJD: 2.25, FKP: 0.79, GBP: 0.79, GEL: 2.65, GHS: 12.4, GIP: 0.79, GMD: 67,
  GNF: 8600, GTQ: 7.8, GYD: 209, HKD: 7.82, HNL: 24.6, HRK: 7.02, HTG: 132, HUF: 355, IDR: 15600, ILS: 3.65,
  INR: 83.1, IQD: 1310, IRR: 42000, ISK: 138, JMD: 155, JOD: 0.71, JPY: 149.5, KES: 145, KGS: 89, KHR: 4100,
  KMF: 452, KPW: 900, KRW: 1330, KWD: 0.31, KYD: 0.83, KZT: 450, LAK: 20800, LBP: 15000, LKR: 312, LRD: 190,
  LSL: 19, LYD: 4.8, MAD: 10.1, MDL: 17.8, MGA: 4500, MKD: 56.5, MMK: 2100, MNT: 3450, MOP: 8.05, MRU: 39.5,
  MUR: 45, MVR: 15.4, MWK: 1680, MXN: 17.1, MYR: 4.75, MZN: 63.8, NAD: 19, NGN: 1500, NIO: 36.6, NOK: 10.5,
  NPR: 133, NZD: 1.63, OMR: 0.38, PAB: 1, PEN: 3.75, PGK: 3.75, PHP: 56, PKR: 278, PLN: 3.98, PYG: 7280,
  QAR: 3.64, RON: 4.6, RSD: 107, RUB: 91, RWF: 1270, SAR: 3.75, SBD: 8.5, SCR: 13.5, SDG: 600, SEK: 10.3,
  SGD: 1.34, SHP: 0.79, SLE: 22.5, SLL: 22500, SOS: 570, SRD: 35, SSP: 1000, STN: 22.5, SYP: 2500, SZL: 19,
  THB: 35.8, TJS: 10.9, TMT: 3.5, TND: 3.12, TOP: 2.35, TRY: 31, TTD: 6.78, TWD: 31.5, TZS: 2530, UAH: 38,
  UGX: 3900, USD: 1, UYU: 39.2, UZS: 12400, VES: 36.2, VND: 24500, VUV: 121, WST: 2.7, XAF: 603, XCD: 2.7,
  XOF: 603, XPF: 110, YER: 250, ZAR: 19, ZMW: 25, ZWL: 322,
};

export type CurrencyCode =
  | "AED" | "AFN" | "ALL" | "AMD" | "ANG" | "AOA" | "ARS" | "AUD" | "AWG" | "AZN"
  | "BAM" | "BBD" | "BDT" | "BGN" | "BHD" | "BIF" | "BMD" | "BND" | "BOB" | "BRL"
  | "BSD" | "BTN" | "BWP" | "BYN" | "BZD" | "CAD" | "CDF" | "CHF" | "CLP" | "CNY"
  | "COP" | "CRC" | "CUP" | "CVE" | "CZK" | "DJF" | "DKK" | "DOP" | "DZD" | "EGP"
  | "ERN" | "ETB" | "EUR" | "FJD" | "FKP" | "GBP" | "GEL" | "GHS" | "GIP" | "GMD"
  | "GNF" | "GTQ" | "GYD" | "HKD" | "HNL" | "HRK" | "HTG" | "HUF" | "IDR" | "ILS"
  | "INR" | "IQD" | "IRR" | "ISK" | "JMD" | "JOD" | "JPY" | "KES" | "KGS" | "KHR"
  | "KMF" | "KPW" | "KRW" | "KWD" | "KYD" | "KZT" | "LAK" | "LBP" | "LKR" | "LRD"
  | "LSL" | "LYD" | "MAD" | "MDL" | "MGA" | "MKD" | "MMK" | "MNT" | "MOP" | "MRU"
  | "MUR" | "MVR" | "MWK" | "MXN" | "MYR" | "MZN" | "NAD" | "NGN" | "NIO" | "NOK"
  | "NPR" | "NZD" | "OMR" | "PAB" | "PEN" | "PGK" | "PHP" | "PKR" | "PLN" | "PYG"
  | "QAR" | "RON" | "RSD" | "RUB" | "RWF" | "SAR" | "SBD" | "SCR" | "SDG" | "SEK"
  | "SGD" | "SHP" | "SLE" | "SLL" | "SOS" | "SRD" | "SSP" | "STN" | "SYP" | "SZL"
  | "THB" | "TJS" | "TMT" | "TND" | "TOP" | "TRY" | "TTD" | "TWD" | "TZS" | "UAH"
  | "UGX" | "USD" | "UYU" | "UZS" | "VES" | "VND" | "VUV" | "WST" | "XAF" | "XCD"
  | "XOF" | "XPF" | "YER" | "ZAR" | "ZMW" | "ZWL";

export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  AED: "د.إ", AFN: "؋", ALL: "L", AMD: "֏", ANG: "ƒ",
  AOA: "Kz", ARS: "$", AUD: "A$", AWG: "ƒ", AZN: "₼",
  BAM: "KM", BBD: "$", BDT: "৳", BGN: "лв", BHD: ".د.ب",
  BIF: "FBu", BMD: "$", BND: "$", BOB: "Bs.", BRL: "R$",
  BSD: "$", BTN: "Nu.", BWP: "P", BYN: "Br", BZD: "BZ$",
  CAD: "C$", CDF: "FC", CHF: "CHF", CLP: "$", CNY: "¥",
  COP: "$", CRC: "₡", CUP: "$", CVE: "$", CZK: "Kč",
  DJF: "Fdj", DKK: "kr", DOP: "RD$", DZD: "دج", EGP: "E£",
  ERN: "Nfk", ETB: "Br", EUR: "€", FJD: "$", FKP: "£",
  GBP: "£", GEL: "₾", GHS: "GH₵", GIP: "£", GMD: "D",
  GNF: "FG", GTQ: "Q", GYD: "$", HKD: "HK$", HNL: "L",
  HRK: "kn", HTG: "G", HUF: "Ft", IDR: "Rp", ILS: "₪",
  INR: "₹", IQD: "ع.د", IRR: "﷼", ISK: "kr", JMD: "J$",
  JOD: "د.ا", JPY: "¥", KES: "KSh", KGS: "с", KHR: "៛",
  KMF: "CF", KPW: "₩", KRW: "₩", KWD: "د.ك", KYD: "$",
  KZT: "₸", LAK: "₭", LBP: "ل.ل", LKR: "₨", LRD: "$",
  LSL: "M", LYD: "ل.د", MAD: "MAD", MDL: "L", MGA: "Ar",
  MKD: "ден", MMK: "K", MNT: "₮", MOP: "P", MRU: "UM",
  MUR: "₨", MVR: ".ރ", MWK: "MK", MXN: "$", MYR: "RM",
  MZN: "MT", NAD: "$", NGN: "₦", NIO: "C$", NOK: "kr",
  NPR: "₨", NZD: "NZ$", OMR: "﷼", PAB: "B/.", PEN: "S/.",
  PGK: "K", PHP: "₱", PKR: "₨", PLN: "zł", PYG: "Gs",
  QAR: "﷼", RON: "lei", RSD: "дин.", RUB: "₽", RWF: "RF",
  SAR: "﷼", SBD: "$", SCR: "₨", SDG: "ج.س.", SEK: "kr",
  SGD: "S$", SHP: "£", SLE: "Le", SLL: "Le", SOS: "S",
  SRD: "$", SSP: "£", STN: "Db", SYP: "£", SZL: "L",
  THB: "฿", TJS: "SM", TMT: "T", TND: "د.ت", TOP: "T$",
  TRY: "₺", TTD: "TT$", TWD: "NT$", TZS: "TSh", UAH: "₴",
  UGX: "USh", USD: "$", UYU: "$U", UZS: "so'm", VES: "Bs.S",
  VND: "₫", VUV: "VT", WST: "WS$", XAF: "FCFA", XCD: "$",
  XOF: "CFA", XPF: "₣", YER: "﷼", ZAR: "R", ZMW: "ZK",
  ZWL: "$",
};

export const CURRENCY_FORMATTERS: Record<string, Intl.NumberFormat> = {
  AED: new Intl.NumberFormat("ar-AE", { style: "currency", currency: "AED" }),
  AFN: new Intl.NumberFormat("ps-AF", { style: "currency", currency: "AFN" }),
  ALL: new Intl.NumberFormat("sq-AL", { style: "currency", currency: "ALL" }),
  AMD: new Intl.NumberFormat("hy-AM", { style: "currency", currency: "AMD" }),
  ANG: new Intl.NumberFormat("nl-CW", { style: "currency", currency: "ANG" }),
  AOA: new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA" }),
  ARS: new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }),
  AUD: new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }),
  AWG: new Intl.NumberFormat("nl-AW", { style: "currency", currency: "AWG" }),
  AZN: new Intl.NumberFormat("az-AZ", { style: "currency", currency: "AZN" }),
  BAM: new Intl.NumberFormat("bs-BA", { style: "currency", currency: "BAM" }),
  BBD: new Intl.NumberFormat("en-BB", { style: "currency", currency: "BBD" }),
  BDT: new Intl.NumberFormat("bn-BD", { style: "currency", currency: "BDT" }),
  BGN: new Intl.NumberFormat("bg-BG", { style: "currency", currency: "BGN" }),
  BHD: new Intl.NumberFormat("ar-BH", { style: "currency", currency: "BHD" }),
  BIF: new Intl.NumberFormat("rn-BI", { style: "currency", currency: "BIF" }),
  BMD: new Intl.NumberFormat("en-BM", { style: "currency", currency: "BMD" }),
  BND: new Intl.NumberFormat("ms-BN", { style: "currency", currency: "BND" }),
  BOB: new Intl.NumberFormat("es-BO", { style: "currency", currency: "BOB" }),
  BRL: new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }),
  BSD: new Intl.NumberFormat("en-BS", { style: "currency", currency: "BSD" }),
  BTN: new Intl.NumberFormat("dz-BT", { style: "currency", currency: "BTN" }),
  BWP: new Intl.NumberFormat("en-BW", { style: "currency", currency: "BWP" }),
  BYN: new Intl.NumberFormat("be-BY", { style: "currency", currency: "BYN" }),
  BZD: new Intl.NumberFormat("en-BZ", { style: "currency", currency: "BZD" }),
  CAD: new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }),
  CDF: new Intl.NumberFormat("fr-CD", { style: "currency", currency: "CDF" }),
  CHF: new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }),
  CLP: new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }),
  CNY: new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" }),
  COP: new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }),
  CRC: new Intl.NumberFormat("es-CR", { style: "currency", currency: "CRC" }),
  CUP: new Intl.NumberFormat("es-CU", { style: "currency", currency: "CUP" }),
  CVE: new Intl.NumberFormat("pt-CV", { style: "currency", currency: "CVE" }),
  CZK: new Intl.NumberFormat("cs-CZ", { style: "currency", currency: "CZK" }),
  DJF: new Intl.NumberFormat("fr-DJ", { style: "currency", currency: "DJF" }),
  DKK: new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK" }),
  DOP: new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }),
  DZD: new Intl.NumberFormat("ar-DZ", { style: "currency", currency: "DZD" }),
  EGP: new Intl.NumberFormat("ar-EG", { style: "currency", currency: "EGP" }),
  ERN: new Intl.NumberFormat("ti-ER", { style: "currency", currency: "ERN" }),
  ETB: new Intl.NumberFormat("am-ET", { style: "currency", currency: "ETB" }),
  EUR: new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }),
  FJD: new Intl.NumberFormat("en-FJ", { style: "currency", currency: "FJD" }),
  FKP: new Intl.NumberFormat("en-FK", { style: "currency", currency: "FKP" }),
  GBP: new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }),
  GEL: new Intl.NumberFormat("ka-GE", { style: "currency", currency: "GEL" }),
  GHS: new Intl.NumberFormat("ak-GH", { style: "currency", currency: "GHS" }),
  GIP: new Intl.NumberFormat("en-GI", { style: "currency", currency: "GIP" }),
  GMD: new Intl.NumberFormat("en-GM", { style: "currency", currency: "GMD" }),
  GNF: new Intl.NumberFormat("fr-GN", { style: "currency", currency: "GNF" }),
  GTQ: new Intl.NumberFormat("es-GT", { style: "currency", currency: "GTQ" }),
  GYD: new Intl.NumberFormat("en-GY", { style: "currency", currency: "GYD" }),
  HKD: new Intl.NumberFormat("zh-HK", { style: "currency", currency: "HKD" }),
  HNL: new Intl.NumberFormat("es-HN", { style: "currency", currency: "HNL" }),
  HRK: new Intl.NumberFormat("hr-HR", { style: "currency", currency: "HRK" }),
  HTG: new Intl.NumberFormat("fr-HT", { style: "currency", currency: "HTG" }),
  HUF: new Intl.NumberFormat("hu-HU", { style: "currency", currency: "HUF" }),
  IDR: new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }),
  ILS: new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS" }),
  INR: new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }),
  IQD: new Intl.NumberFormat("ar-IQ", { style: "currency", currency: "IQD" }),
  IRR: new Intl.NumberFormat("fa-IR", { style: "currency", currency: "IRR" }),
  ISK: new Intl.NumberFormat("is-IS", { style: "currency", currency: "ISK" }),
  JMD: new Intl.NumberFormat("en-JM", { style: "currency", currency: "JMD" }),
  JOD: new Intl.NumberFormat("ar-JO", { style: "currency", currency: "JOD" }),
  JPY: new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }),
  KES: new Intl.NumberFormat("sw-KE", { style: "currency", currency: "KES" }),
  KGS: new Intl.NumberFormat("ky-KG", { style: "currency", currency: "KGS" }),
  KHR: new Intl.NumberFormat("km-KH", { style: "currency", currency: "KHR" }),
  KMF: new Intl.NumberFormat("ar-KM", { style: "currency", currency: "KMF" }),
  KPW: new Intl.NumberFormat("ko-KP", { style: "currency", currency: "KPW" }),
  KRW: new Intl.NumberFormat("ko-KR", { style: "currency", currency: "KRW" }),
  KWD: new Intl.NumberFormat("ar-KW", { style: "currency", currency: "KWD" }),
  KYD: new Intl.NumberFormat("en-KY", { style: "currency", currency: "KYD" }),
  KZT: new Intl.NumberFormat("kk-KZ", { style: "currency", currency: "KZT" }),
  LAK: new Intl.NumberFormat("lo-LA", { style: "currency", currency: "LAK" }),
  LBP: new Intl.NumberFormat("ar-LB", { style: "currency", currency: "LBP" }),
  LKR: new Intl.NumberFormat("si-LK", { style: "currency", currency: "LKR" }),
  LRD: new Intl.NumberFormat("en-LR", { style: "currency", currency: "LRD" }),
  LSL: new Intl.NumberFormat("en-LS", { style: "currency", currency: "LSL" }),
  LYD: new Intl.NumberFormat("ar-LY", { style: "currency", currency: "LYD" }),
  MAD: new Intl.NumberFormat("ar-MA", { style: "currency", currency: "MAD" }),
  MDL: new Intl.NumberFormat("ro-MD", { style: "currency", currency: "MDL" }),
  MGA: new Intl.NumberFormat("mg-MG", { style: "currency", currency: "MGA" }),
  MKD: new Intl.NumberFormat("mk-MK", { style: "currency", currency: "MKD" }),
  MMK: new Intl.NumberFormat("my-MM", { style: "currency", currency: "MMK" }),
  MNT: new Intl.NumberFormat("mn-MN", { style: "currency", currency: "MNT" }),
  MOP: new Intl.NumberFormat("zh-MO", { style: "currency", currency: "MOP" }),
  MRU: new Intl.NumberFormat("ar-MR", { style: "currency", currency: "MRU" }),
  MUR: new Intl.NumberFormat("en-MU", { style: "currency", currency: "MUR" }),
  MVR: new Intl.NumberFormat("dv-MV", { style: "currency", currency: "MVR" }),
  MWK: new Intl.NumberFormat("en-MW", { style: "currency", currency: "MWK" }),
  MXN: new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }),
  MYR: new Intl.NumberFormat("ms-MY", { style: "currency", currency: "MYR" }),
  MZN: new Intl.NumberFormat("pt-MZ", { style: "currency", currency: "MZN" }),
  NAD: new Intl.NumberFormat("en-NA", { style: "currency", currency: "NAD" }),
  NGN: new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }),
  NIO: new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }),
  NOK: new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK" }),
  NPR: new Intl.NumberFormat("ne-NP", { style: "currency", currency: "NPR" }),
  NZD: new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }),
  OMR: new Intl.NumberFormat("ar-OM", { style: "currency", currency: "OMR" }),
  PAB: new Intl.NumberFormat("es-PA", { style: "currency", currency: "PAB" }),
  PEN: new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }),
  PGK: new Intl.NumberFormat("en-PG", { style: "currency", currency: "PGK" }),
  PHP: new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }),
  PKR: new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR" }),
  PLN: new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }),
  PYG: new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG" }),
  QAR: new Intl.NumberFormat("ar-QA", { style: "currency", currency: "QAR" }),
  RON: new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON" }),
  RSD: new Intl.NumberFormat("sr-RS", { style: "currency", currency: "RSD" }),
  RUB: new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" }),
  RWF: new Intl.NumberFormat("rw-RW", { style: "currency", currency: "RWF" }),
  SAR: new Intl.NumberFormat("ar-SA", { style: "currency", currency: "SAR" }),
  SBD: new Intl.NumberFormat("en-SB", { style: "currency", currency: "SBD" }),
  SCR: new Intl.NumberFormat("en-SC", { style: "currency", currency: "SCR" }),
  SDG: new Intl.NumberFormat("ar-SD", { style: "currency", currency: "SDG" }),
  SEK: new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" }),
  SGD: new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }),
  SHP: new Intl.NumberFormat("en-SH", { style: "currency", currency: "SHP" }),
  SLE: new Intl.NumberFormat("en-SL", { style: "currency", currency: "SLE" }),
  SLL: new Intl.NumberFormat("en-SL", { style: "currency", currency: "SLL" }),
  SOS: new Intl.NumberFormat("so-SO", { style: "currency", currency: "SOS" }),
  SRD: new Intl.NumberFormat("nl-SR", { style: "currency", currency: "SRD" }),
  SSP: new Intl.NumberFormat("en-SS", { style: "currency", currency: "SSP" }),
  STN: new Intl.NumberFormat("pt-ST", { style: "currency", currency: "STN" }),
  SYP: new Intl.NumberFormat("ar-SY", { style: "currency", currency: "SYP" }),
  SZL: new Intl.NumberFormat("en-SZ", { style: "currency", currency: "SZL" }),
  THB: new Intl.NumberFormat("th-TH", { style: "currency", currency: "THB" }),
  TJS: new Intl.NumberFormat("tg-TJ", { style: "currency", currency: "TJS" }),
  TMT: new Intl.NumberFormat("tk-TM", { style: "currency", currency: "TMT" }),
  TND: new Intl.NumberFormat("ar-TN", { style: "currency", currency: "TND" }),
  TOP: new Intl.NumberFormat("to-TO", { style: "currency", currency: "TOP" }),
  TRY: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }),
  TTD: new Intl.NumberFormat("en-TT", { style: "currency", currency: "TTD" }),
  TWD: new Intl.NumberFormat("zh-TW", { style: "currency", currency: "TWD" }),
  TZS: new Intl.NumberFormat("sw-TZ", { style: "currency", currency: "TZS" }),
  UAH: new Intl.NumberFormat("uk-UA", { style: "currency", currency: "UAH" }),
  UGX: new Intl.NumberFormat("sw-UG", { style: "currency", currency: "UGX" }),
  USD: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }),
  UYU: new Intl.NumberFormat("es-UY", { style: "currency", currency: "UYU" }),
  UZS: new Intl.NumberFormat("uz-UZ", { style: "currency", currency: "UZS" }),
  VES: new Intl.NumberFormat("es-VE", { style: "currency", currency: "VES" }),
  VND: new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }),
  VUV: new Intl.NumberFormat("bi-VU", { style: "currency", currency: "VUV" }),
  WST: new Intl.NumberFormat("sm-WS", { style: "currency", currency: "WST" }),
  XAF: new Intl.NumberFormat("fr-CM", { style: "currency", currency: "XAF" }),
  XCD: new Intl.NumberFormat("en-GD", { style: "currency", currency: "XCD" }),
  XOF: new Intl.NumberFormat("fr-CI", { style: "currency", currency: "XOF" }),
  XPF: new Intl.NumberFormat("fr-PF", { style: "currency", currency: "XPF" }),
  YER: new Intl.NumberFormat("ar-YE", { style: "currency", currency: "YER" }),
  ZAR: new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }),
  ZMW: new Intl.NumberFormat("en-ZM", { style: "currency", currency: "ZMW" }),
  ZWL: new Intl.NumberFormat("en-ZW", { style: "currency", currency: "ZWL" }),
};

export const DEFAULT_ITEMS_PER_PAGE = 20;

export const PLATFORM_ICON_MAP: Record<string, string> = {
  google_ads: "https://assets.apprevbooster.com/integrations/googleAds.png",
  google_admob: "https://assets.apprevbooster.com/integrations/googleAdMob.svg",
  facebook: "https://assets.apprevbooster.com/integrations/meta.png",
  meta_ads: "https://assets.apprevbooster.com/integrations/meta.png",
  apple_ads: "https://assets.apprevbooster.com/integrations/appStore.png",
  apple_search_ads: "https://assets.apprevbooster.com/integrations/appStore.png",
  apple_iap: "https://assets.apprevbooster.com/integrations/appleIcon.svg",
  google_iap: "https://assets.apprevbooster.com/integrations/playStore.webp",
};

export const PLATFORM_OPTIONS = [
  { label: "GoogleAds", value: "google_ads" },
  { label: "AdMob", value: "google_admob" },
  { label: "AppleAds", value: "apple_ads" },
  { label: "AppleIAP", value: "apple_iap" },
  { label: "GoogleIAP", value: "google_iap" },
  { label: "MetaAds", value: "meta_ads" },
];

export const PLATFORM_LABEL_MAPPER: Record<string, string> = {
  google_ads: "Google Ads",
  google_admob: "Google AdMob",
  apple_ads: "Apple Ads",
  apple_search_ads: "Apple Search Ads",
  apple_iap: "Apple IAP",
  google_iap: "Google IAP",
  meta_ads: "Meta Ads",
};

export const DEFAULT_AVATAR = defaultAvatarUrl;

export const DEFAULT_AVATAR_FOR_DELETED_USER =
  "https://assets.apprevbooster.com/deleted_user.png";

export const LANGUAGES_OPTIONS = [
  { code: "en", name: "English", flagCode: "US" },
  { code: "de", name: "Deutsch", flagCode: "DE" },
  { code: "fr", name: "Français", flagCode: "FR" },
  { code: "es", name: "Español", flagCode: "ES" },
  { code: "ja", name: "日本語", flagCode: "JP" },
];

export const APP_PLATFORM_LOGO_MAP: Record<string, string> = {
  android: "", // Android logo
  ios: "https://assets.apprevbooster.com/integrations/appleIcon.svg", // Apple logo
  google: "https://assets.apprevbooster.com/integrations/googleIcon.svg",
  apple: "https://assets.apprevbooster.com/integrations/appleIcon.svg",
  playStore: "https://assets.apprevbooster.com/integrations/playStoreIcon.svg",
  google_ads: "https://assets.apprevbooster.com/integrations/googleAds.png",
  google_admob: "https://assets.apprevbooster.com/integrations/googleAdMob.svg",
  app_store: "https://assets.apprevbooster.com/integrations/appStore.png",
  meta: "https://assets.apprevbooster.com/integrations/meta.png",
  meta_ads: "https://assets.apprevbooster.com/integrations/meta.png",
};


export const ROLES: UserRole[] = ["super_admin", "admin", "sub_admin"];

export const CURRENCIES: TCurrency[] = [
  "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN",
  "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BRL",
  "BSD", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHF", "CLP", "CNY",
  "COP", "CRC", "CUP", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP",
  "ERN", "ETB", "EUR", "FJD", "FKP", "GBP", "GEL", "GHS", "GIP", "GMD",
  "GNF", "GTQ", "GYD", "HKD", "HNL", "HRK", "HTG", "HUF", "IDR", "ILS",
  "INR", "IQD", "IRR", "ISK", "JMD", "JOD", "JPY", "KES", "KGS", "KHR",
  "KMF", "KPW", "KRW", "KWD", "KYD", "KZT", "LAK", "LBP", "LKR", "LRD",
  "LSL", "LYD", "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MRU",
  "MUR", "MVR", "MWK", "MXN", "MYR", "MZN", "NAD", "NGN", "NIO", "NOK",
  "NPR", "NZD", "OMR", "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG",
  "QAR", "RON", "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SDG", "SEK",
  "SGD", "SHP", "SLE", "SLL", "SOS", "SRD", "SSP", "STN", "SYP", "SZL",
  "THB", "TJS", "TMT", "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH",
  "UGX", "USD", "UYU", "UZS", "VES", "VND", "VUV", "WST", "XAF", "XCD",
  "XOF", "XPF", "YER", "ZAR", "ZMW", "ZWL",
];

export const CURRENCIES_OPTIONS = [
  { code: "AED", symbol: "د.إ", name: "United Arab Emirates Dirham" },
  { code: "AFN", symbol: "؋", name: "Afghan Afghani" },
  { code: "ALL", symbol: "L", name: "Albanian Lek" },
  { code: "AMD", symbol: "֏", name: "Armenian Dram" },
  { code: "ANG", symbol: "ƒ", name: "Netherlands Antillean Guilder" },
  { code: "AOA", symbol: "Kz", name: "Angolan Kwanza" },
  { code: "ARS", symbol: "$", name: "Argentine Peso" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "AWG", symbol: "ƒ", name: "Aruban Florin" },
  { code: "AZN", symbol: "₼", name: "Azerbaijani Manat" },
  { code: "BAM", symbol: "KM", name: "Bosnia-Herzegovina Convertible Mark" },
  { code: "BBD", symbol: "$", name: "Barbadian Dollar" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "BGN", symbol: "лв", name: "Bulgarian Lev" },
  { code: "BHD", symbol: ".د.ب", name: "Bahraini Dinar" },
  { code: "BIF", symbol: "FBu", name: "Burundian Franc" },
  { code: "BMD", symbol: "$", name: "Bermudan Dollar" },
  { code: "BND", symbol: "$", name: "Brunei Dollar" },
  { code: "BOB", symbol: "Bs.", name: "Bolivian Boliviano" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "BSD", symbol: "$", name: "Bahamian Dollar" },
  { code: "BTN", symbol: "Nu.", name: "Bhutanese Ngultrum" },
  { code: "BWP", symbol: "P", name: "Botswanan Pula" },
  { code: "BYN", symbol: "Br", name: "Belarusian Ruble" },
  { code: "BZD", symbol: "BZ$", name: "Belize Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "CDF", symbol: "FC", name: "Congolese Franc" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "CLP", symbol: "$", name: "Chilean Peso" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "COP", symbol: "$", name: "Colombian Peso" },
  { code: "CRC", symbol: "₡", name: "Costa Rican Colón" },
  { code: "CUP", symbol: "$", name: "Cuban Peso" },
  { code: "CVE", symbol: "$", name: "Cape Verdean Escudo" },
  { code: "CZK", symbol: "Kč", name: "Czech Koruna" },
  { code: "DJF", symbol: "Fdj", name: "Djiboutian Franc" },
  { code: "DKK", symbol: "kr", name: "Danish Krone" },
  { code: "DOP", symbol: "RD$", name: "Dominican Peso" },
  { code: "DZD", symbol: "دج", name: "Algerian Dinar" },
  { code: "EGP", symbol: "E£", name: "Egyptian Pound" },
  { code: "ERN", symbol: "Nfk", name: "Eritrean Nakfa" },
  { code: "ETB", symbol: "Br", name: "Ethiopian Birr" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "FJD", symbol: "$", name: "Fijian Dollar" },
  { code: "FKP", symbol: "£", name: "Falkland Islands Pound" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "GEL", symbol: "₾", name: "Georgian Lari" },
  { code: "GHS", symbol: "GH₵", name: "Ghanaian Cedi" },
  { code: "GIP", symbol: "£", name: "Gibraltar Pound" },
  { code: "GMD", symbol: "D", name: "Gambian Dalasi" },
  { code: "GNF", symbol: "FG", name: "Guinean Franc" },
  { code: "GTQ", symbol: "Q", name: "Guatemalan Quetzal" },
  { code: "GYD", symbol: "$", name: "Guyanaese Dollar" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { code: "HNL", symbol: "L", name: "Honduran Lempira" },
  { code: "HRK", symbol: "kn", name: "Croatian Kuna" },
  { code: "HTG", symbol: "G", name: "Haitian Gourde" },
  { code: "HUF", symbol: "Ft", name: "Hungarian Forint" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "ILS", symbol: "₪", name: "Israeli New Shekel" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "IQD", symbol: "ع.د", name: "Iraqi Dinar" },
  { code: "IRR", symbol: "﷼", name: "Iranian Rial" },
  { code: "ISK", symbol: "kr", name: "Icelandic Króna" },
  { code: "JMD", symbol: "J$", name: "Jamaican Dollar" },
  { code: "JOD", symbol: "د.ا", name: "Jordanian Dinar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "KGS", symbol: "с", name: "Kyrgystani Som" },
  { code: "KHR", symbol: "៛", name: "Cambodian Riel" },
  { code: "KMF", symbol: "CF", name: "Comorian Franc" },
  { code: "KPW", symbol: "₩", name: "North Korean Won" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
  { code: "KWD", symbol: "د.ك", name: "Kuwaiti Dinar" },
  { code: "KYD", symbol: "$", name: "Cayman Islands Dollar" },
  { code: "KZT", symbol: "₸", name: "Kazakhstani Tenge" },
  { code: "LAK", symbol: "₭", name: "Laotian Kip" },
  { code: "LBP", symbol: "ل.ل", name: "Lebanese Pound" },
  { code: "LKR", symbol: "₨", name: "Sri Lankan Rupee" },
  { code: "LRD", symbol: "$", name: "Liberian Dollar" },
  { code: "LSL", symbol: "M", name: "Lesotho Loti" },
  { code: "LYD", symbol: "ل.د", name: "Libyan Dinar" },
  { code: "MAD", symbol: "MAD", name: "Moroccan Dirham" },
  { code: "MDL", symbol: "L", name: "Moldovan Leu" },
  { code: "MGA", symbol: "Ar", name: "Malagasy Ariary" },
  { code: "MKD", symbol: "ден", name: "Macedonian Denar" },
  { code: "MMK", symbol: "K", name: "Myanmar Kyat" },
  { code: "MNT", symbol: "₮", name: "Mongolian Tugrik" },
  { code: "MOP", symbol: "P", name: "Macanese Pataca" },
  { code: "MRU", symbol: "UM", name: "Mauritanian Ouguiya" },
  { code: "MUR", symbol: "₨", name: "Mauritian Rupee" },
  { code: "MVR", symbol: ".ރ", name: "Maldivian Rufiyaa" },
  { code: "MWK", symbol: "MK", name: "Malawian Kwacha" },
  { code: "MXN", symbol: "$", name: "Mexican Peso" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "MZN", symbol: "MT", name: "Mozambican Metical" },
  { code: "NAD", symbol: "$", name: "Namibian Dollar" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "NIO", symbol: "C$", name: "Nicaraguan Córdoba" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
  { code: "NPR", symbol: "₨", name: "Nepalese Rupee" },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar" },
  { code: "OMR", symbol: "﷼", name: "Omani Rial" },
  { code: "PAB", symbol: "B/.", name: "Panamanian Balboa" },
  { code: "PEN", symbol: "S/.", name: "Peruvian Sol" },
  { code: "PGK", symbol: "K", name: "Papua New Guinean Kina" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "PKR", symbol: "₨", name: "Pakistani Rupee" },
  { code: "PLN", symbol: "zł", name: "Polish Zloty" },
  { code: "PYG", symbol: "Gs", name: "Paraguayan Guarani" },
  { code: "QAR", symbol: "﷼", name: "Qatari Rial" },
  { code: "RON", symbol: "lei", name: "Romanian Leu" },
  { code: "RSD", symbol: "дин.", name: "Serbian Dinar" },
  { code: "RUB", symbol: "₽", name: "Russian Ruble" },
  { code: "RWF", symbol: "RF", name: "Rwandan Franc" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "SBD", symbol: "$", name: "Solomon Islands Dollar" },
  { code: "SCR", symbol: "₨", name: "Seychellois Rupee" },
  { code: "SDG", symbol: "ج.س.", name: "Sudanese Pound" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "SHP", symbol: "£", name: "St. Helena Pound" },
  { code: "SLE", symbol: "Le", name: "Sierra Leonean Leone" },
  { code: "SLL", symbol: "Le", name: "Sierra Leonean Leone" },
  { code: "SOS", symbol: "S", name: "Somali Shilling" },
  { code: "SRD", symbol: "$", name: "Surinamese Dollar" },
  { code: "SSP", symbol: "£", name: "South Sudanese Pound" },
  { code: "STN", symbol: "Db", name: "São Tomé & Príncipe Dobra" },
  { code: "SYP", symbol: "£", name: "Syrian Pound" },
  { code: "SZL", symbol: "L", name: "Swazi Lilangeni" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "TJS", symbol: "SM", name: "Tajikistani Somoni" },
  { code: "TMT", symbol: "T", name: "Turkmenistani Manat" },
  { code: "TND", symbol: "د.ت", name: "Tunisian Dinar" },
  { code: "TOP", symbol: "T$", name: "Tongan Paʻanga" },
  { code: "TRY", symbol: "₺", name: "Turkish Lira" },
  { code: "TTD", symbol: "TT$", name: "Trinidad & Tobago Dollar" },
  { code: "TWD", symbol: "NT$", name: "New Taiwan Dollar" },
  { code: "TZS", symbol: "TSh", name: "Tanzanian Shilling" },
  { code: "UAH", symbol: "₴", name: "Ukrainian Hryvnia" },
  { code: "UGX", symbol: "USh", name: "Ugandan Shilling" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "UYU", symbol: "$U", name: "Uruguayan Peso" },
  { code: "UZS", symbol: "so'm", name: "Uzbekistani Som" },
  { code: "VES", symbol: "Bs.S", name: "Venezuelan Bolívar" },
  { code: "VND", symbol: "₫", name: "Vietnamese Dong" },
  { code: "VUV", symbol: "VT", name: "Vanuatu Vatu" },
  { code: "WST", symbol: "WS$", name: "Samoan Tala" },
  { code: "XAF", symbol: "FCFA", name: "Central African CFA Franc" },
  { code: "XCD", symbol: "$", name: "East Caribbean Dollar" },
  { code: "XOF", symbol: "CFA", name: "West African CFA Franc" },
  { code: "XPF", symbol: "₣", name: "CFP Franc" },
  { code: "YER", symbol: "﷼", name: "Yemeni Rial" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "ZMW", symbol: "ZK", name: "Zambian Kwacha" },
  { code: "ZWL", symbol: "$", name: "Zimbabwean Dollar" },
];

export const customSelectStyles = <T>(isDark = false): StylesConfig<
  T,
  boolean,
  GroupBase<T>
> => {
  const text = isDark ? "#f3f4f6" : "#111827";
  const bg = isDark ? "#262626" : "#ffffff";
  const border = isDark ? "#404040" : "#d1d5db";
  const hoverBg = isDark ? "#303030" : "#f3f4f6";
  const selectedBg = isDark ? "#303030" : "#f3f4f6";
  const multiValueBg = isDark ? "#333333" : "#e5e7eb";
  const focusShadow = isDark ? "0 0 0 2px #525252" : "0 0 0 2px #9ca3af";
  const sharedFont = {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
  };
  return {
    control: (base: CSSObject, state: { isFocused: boolean }) => ({
      ...base,
      minHeight: "32px",
      borderRadius: "0.5rem",
      backgroundColor: bg,
      borderColor: state.isFocused ? "transparent" : border,
      boxShadow: state.isFocused ? focusShadow : "none",
      ...sharedFont,
      color: text,
      "&:hover": {
        borderColor: state.isFocused ? "transparent" : border,
      },
    }),
    menu: (base: CSSObject) => ({
      ...base,
      zIndex: "20",
      backgroundColor: bg,
      ...sharedFont,
      color: text,
    }),
    menuList: (base: CSSObject) => ({
      ...base,
      backgroundColor: bg,
    }),
    option: (
      base: CSSObject,
      state: { isSelected: boolean; isFocused: boolean }
    ) => ({
      ...base,
      backgroundColor: state.isSelected || state.isFocused ? selectedBg : "transparent",
      color: text,
      ...sharedFont,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: hoverBg,
      },
    }),
    multiValue: (base: CSSObject) => ({
      ...base,
      backgroundColor: multiValueBg,
      ...sharedFont,
      color: text,
    }),
    multiValueLabel: (base: CSSObject) => ({
      ...base,
      color: text,
      ...sharedFont,
    }),
    multiValueRemove: (base: CSSObject) => ({
      ...base,
      color: text,
      ...sharedFont,
      "&:hover": {
        backgroundColor: border,
        color: text,
      },
    }),
    clearIndicator: (base: CSSObject) => ({
      ...base,
      cursor: "pointer",
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
    dropdownIndicator: (base: CSSObject) => ({
      ...base,
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
    indicatorSeparator: (base: CSSObject) => ({
      ...base,
      backgroundColor: border,
    }),
    singleValue: (base: CSSObject) => ({
      ...base,
      cursor: "pointer",
      ...sharedFont,
      color: text,
    }),
    valueContainer: (base: CSSObject) => ({
      ...base,
      cursor: "pointer",
      backgroundColor: bg,
      borderRadius: "1rem !important"
    }),
    input: (base: CSSObject) => ({
      ...base,
      color: text,
    }),
    placeholder: (base: CSSObject) => ({
      ...base,
      color: isDark ? "#6b7280" : "#9ca3af",
    }),
    loadingMessage: (base: CSSObject) => ({
      ...base,
      backgroundColor: bg,
      color: isDark ? "#6b7280" : "#9ca3af",
    }),
    noOptionsMessage: (base: CSSObject) => ({
      ...base,
      backgroundColor: bg,
      color: isDark ? "#6b7280" : "#9ca3af",
    }),
    loadingIndicator: (base: CSSObject) => ({
      ...base,
      color: isDark ? "#6b7280" : "#9ca3af",
    }),
  };
};

export const BIDDING_STRATEGY_OPTIONS = [
  { value: "TARGET_CPA", label: "TARGET CPA" },
  { value: "TARGET_ROAS", label: "TARGET ROAS" },
];

export const BIDDING_STRATEGY_LABEL_MAPPER: { [key: string]: string } = {
  TARGET_CPA: "Target CPA",
  TARGET_ROAS: "Target ROAS",
};

export const getPresets = (t: TFunction<"translation", undefined>) => {
  const yesterday = subDays(new Date(), 1);
  return [
    {
      id: "yesterday",
      label: t("common.yesterday"),
      getRange: () => [yesterday, yesterday],
    },
    {
      id: "last7Days",
      label: t("common.last7Days"),
      getRange: () => [subDays(yesterday, 6), yesterday],
    },
    {
      id: "last30Days",
      label: t("common.last30Days"),
      getRange: () => [subDays(yesterday, 29), yesterday],
    },
    {
      id: "thisWeek",
      label: t("common.thisWeek"),
      getRange: () => {
        const weekEnd = yesterday;
        const weekStart = startOfWeek(weekEnd);
        return [weekStart, weekEnd];
      },
    },
    {
      id: "lastWeek",
      label: t("common.lastWeek"),
      getRange: () => {
        const lastWeekEnd = subDays(startOfWeek(yesterday), 1);
        const lastWeekStart = startOfWeek(lastWeekEnd);
        return [lastWeekStart, lastWeekEnd];
      },
    },
    {
      id: "thisMonth",
      label: t("common.thisMonth"),
      getRange: () => {
        const monthEnd = yesterday;
        const monthStart = startOfMonth(monthEnd);
        return [monthStart, monthEnd];
      },
    },
    {
      id: "lastMonth",
      label: t("common.lastMonth"),
      getRange: () => {
        const lastMonth = subMonths(yesterday, 1);
        return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
      },
    },
    {
      id: "thisYear",
      label: t("common.thisYear"),
      getRange: () => {
        const yearEnd = yesterday;
        const yearStart = startOfYear(yearEnd);
        return [yearStart, yearEnd];
      },
    },
    {
      id: "lastYear",
      label: t("common.lastYear"),
      getRange: () => {
        const lastYear = subYears(yesterday, 1);
        return [startOfYear(lastYear), endOfYear(lastYear)];
      },
    },
    {
      id: "custom",
      label: t("common.custom"),
      getRange: () => [yesterday, yesterday] as [Date, Date],
    },
  ] as IDateRangePreset[];
};

export const DEFAULT_SELECTED_ACCOUNT = {
  label: "All",
  value: "all",
  name: "All",
  email: "",
  id: 0,
  slug: "all",
  last_sync_at: "",
  min_date: "",
  profile_picture: null,
};

export enum PERMISSION_MAPPER {
  SUPER_ADMIN = "super_admin",
  SCHEMA_LEVEL_ACCESS = "schema_level_access",
  DASHBOARD_READ = "dashboard_read",
  APP_COUNTRY_REPORTS_READ = "app_country_reports_read",
  BID_MANAGEMENT_READ = "bid_management_read",
  BID_MANAGEMENT_UPDATE = "bid_management_update",
  APP_READ = "app_read",
  APP_FIND_ONE = "app_find_one",
  APP_CHART_READ = "app_chart_read",
  APP_DETAIL_CHART_READ = "app_detail_chart_read",
  CAMPAIGN_COUNTRY_COST_READ = "campaign_country_cost_read",
  CAMPAIGN_FIND_ONE = "campaign_find_one",
  CAMPAIGN_READ = "campaign_read",
  TEAM_MANAGEMENT_CREATE = "team_management_create",
  TEAM_MANAGEMENT_READ = "team_management_read",
  TEAM_MANAGEMENT_UPDATE = "team_management_update",
  TEAM_MANAGEMENT_DELETE = "team_management_delete",
  ROLE_MANAGEMENT_CREATE = "role_management_create",
  ROLE_MANAGEMENT_READ = "role_management_read",
  ROLE_MANAGEMENT_UPDATE = "role_management_update",
  ROLE_MANAGEMENT_DELETE = "role_management_delete",
  INTEGRATION_CREATE = "integration_create",
  INTEGRATION_READ = "integration_read",
  INTEGRATION_UPDATE = "integration_update",
  INTEGRATION_DELETE = "integration_delete",
  SETTINGS_READ = "settings_read",
  USER_ACTIVITY_READ = "user_activity_read",
  SESSION_ACTIVITY_READ = "session_activity_read",
  INTEGRATION_ADDON_CREATE = "addon_integration_create",
  INTEGRATION_ADDON_DELETE = "addon_integration_delete",
  ORDER_READ = "order_read",
  ORDER_EXPORT = "order_export",
  AI_ASSISTANT_READ = "ai_assistant_read",
  REPORTS_READ = "reports_read",
}

export const ACTION_TYPE_META: Record<
  string,
  { label: string; color: string; showDiff: boolean }
> = {
  LOGIN: { label: "Login", color: "green", showDiff: false },
  LOGOUT: { label: "Logout", color: "red", showDiff: false },
  SIGN_UP: { label: "Sign Up", color: "green", showDiff: false },
  SESSION_CONTINUED: { label: "Session Continued", color: "teal", showDiff: false },
  PROFILE_UPDATE: { label: "Profile Update", color: "blue", showDiff: true },
  INVITE_USER_ACCEPT: {
    label: "Invite Accepted",
    color: "indigo",
    showDiff: false,
  },
  INVITE_USER_EMAIL_SENT: {
    label: "Invite Email Sent",
    color: "indigo",
    showDiff: false,
  },
  INVITE_USER_DELETE: {
    label: "Invite Deleted",
    color: "red",
    showDiff: false,
  },
  RESEND_INVITE: { label: "Resend Invite", color: "indigo", showDiff: false },
  USER_DELETE: { label: "User Deleted", color: "red", showDiff: false },
  PASSWORD_RESET_REQUEST: {
    label: "Password Reset Requested",
    color: "yellow",
    showDiff: false,
  },
  PASSWORD_RESET_COMPLETE: {
    label: "Password Reset Complete",
    color: "green",
    showDiff: false,
  },
  PASSWORD_CHANGE: {
    label: "Password Changed",
    color: "yellow",
    showDiff: false,
  },
  USER_ROLE_CHANGE: {
    label: "User Role Changed",
    color: "blue",
    showDiff: true,
  },
  ROLE_CREATE: { label: "Role Created", color: "green", showDiff: false },
  ROLE_UPDATE: { label: "Role Updated", color: "blue", showDiff: true },
  ROLE_DELETE: { label: "Role Deleted", color: "red", showDiff: false },
  DASHBOARD_PREFERENCE_UPDATE: {
    label: "Dashboard Pref Update",
    color: "blue",
    showDiff: true,
  },
  APP_LIST_PREFERENCE_UPDATE: {
    label: "App List Pref Update",
    color: "blue",
    showDiff: true,
  },
  APP_DETAIL_PREFERENCE_UPDATE: {
    label: "App Detail Pref Update",
    color: "blue",
    showDiff: true,
  },
  APP_COUNTRY_REPORT_PREFERENCE_UPDATE: {
    label: "Country Report Pref Update",
    color: "blue",
    showDiff: true,
  },
  BID_CHANGE: { label: "Bid Change", color: "purple", showDiff: true },
  INTEGRATION_START: {
    label: "Integration Started",
    color: "indigo",
    showDiff: false,
  },
  INTEGRATION_COMPLETE: {
    label: "Integration Complete",
    color: "green",
    showDiff: false,
  },
  INTEGRATION_DELETE: {
    label: "Integration Deleted",
    color: "red",
    showDiff: false,
  },
  INVITE_USER_UPDATE: {
    label: "Invite Updated",
    color: "gray",
    showDiff: true,
  },
  USER_DEACTIVATED: {
    label: "User Deactivated",
    color: "gray",
    showDiff: false,
  },
  USER_ACTIVATED: {
    label: "User Activated",
    color: "green",
    showDiff: false,
  },
  ADDON_ACCOUNT_DELETE: {
    label: "Account Deleted",
    color: "red",
    showDiff: false,
  },
  ONGOING_ACCOUNT_DELETE: {
    label: "Ongoing Account Delete",
    color: "gray",
    showDiff: false,
  },
  ADDON_ACCOUNT_INTEGRATION_START: {
    label: "Addon Account Integration Started",
    color: "blue",
    showDiff: false,
  },
  ADDON_ACCOUNT_INTEGRATION_CANCEL: {
    label: "Addon Account Integration Cancelled",
    color: "red",
    showDiff: false,
  },
  ADDON_ACCOUNT_INTEGRATION_COMPLETE: {
    label: "Addon Account Integration Complete",
    color: "green",
    showDiff: false,
  },
  ENTIRE_TRACKING_DELETE: {
    label: "Entire Tracking Deleted",
    color: "red",
    showDiff: false,
  },
  TRIAL_STARTED: { label: "Free Trial Started", color: "green", showDiff: false },
  TRIAL_ENDED: { label: "Free Trial Ended", color: "yellow", showDiff: false },
  SUBSCRIPTION_CREATED: { label: "Subscription Created", color: "green", showDiff: false },
  SUBSCRIPTION_RENEWED: { label: "Subscription Renewed", color: "green", showDiff: false },
  SUBSCRIPTION_UPGRADED: { label: "Subscription Upgraded", color: "indigo", showDiff: true },
  SUBSCRIPTION_DOWNGRADED: { label: "Subscription Downgraded", color: "yellow", showDiff: true },
  SUBSCRIPTION_CANCELLED: { label: "Subscription Cancelled", color: "red", showDiff: false },
  SUBSCRIPTION_REACTIVATED: { label: "Subscription Reactivated", color: "green", showDiff: false },
  PAYMENT_SUCCEEDED: { label: "Payment Successful", color: "green", showDiff: false },
  PAYMENT_FAILED: { label: "Payment Failed", color: "red", showDiff: false },
  REFUND_REQUESTED: { label: "Refund Requested", color: "yellow", showDiff: false },
  REFUND_PROCESSED: { label: "Refund Processed", color: "blue", showDiff: false },
  // Meta Ads events (backend sends full enum path as action_type)
  "UserActivityEventEnum.META_ADS_CONNECT": {
    label: "Meta Ads Connect",
    color: "blue",
    showDiff: false,
  },
  "UserActivityEventEnum.META_ADS_DISCONNECT": {
    label: "Meta Ads Disconnect",
    color: "red",
    showDiff: false,
  },
  "UserActivityEventEnum.META_ADS_ACCOUNT_SAVE": {
    label: "Meta Ads Account Saved",
    color: "green",
    showDiff: false,
  },
  GOOGLE_ADS_BID_CHANGE: { label: "Google Ads Bid Change", color: "purple", showDiff: true },
  GOOGLE_ADS_BUDGET_CHANGE: { label: "Google Ads Budget Change", color: "blue", showDiff: true },
  GOOGLE_ADS_STATUS_CHANGE: { label: "Google Ads Status Change", color: "yellow", showDiff: true },
  APPLE_ADS_BID_CHANGE: { label: "Apple Ads Bid Change", color: "purple", showDiff: true },
  APPLE_ADS_BUDGET_CHANGE: { label: "Apple Ads Budget Change", color: "blue", showDiff: true },
  APPLE_ADS_STATUS_CHANGE: { label: "Apple Ads Status Change", color: "yellow", showDiff: true },
  META_ADS_BID_CHANGE: { label: "Meta Ads Bid Change", color: "purple", showDiff: true },
  META_ADS_BUDGET_CHANGE: { label: "Meta Ads Budget Change", color: "blue", showDiff: true },
  META_ADS_STATUS_CHANGE: { label: "Meta Ads Status Change", color: "yellow", showDiff: true },
};

export const IS_OWNER_FILTER_OPTIONS = [
  { value: "all", label: "All Team Members" },
  { value: "owner", label: "Owner" },
  { value: "not_owner", label: "Not Owner" },
];

export enum LOCAL_STORAGE_KEYS {
  ADD_ON = "addon",
  PLATFORM = "platform",
  INTEGRATING_PLATFORM = "integrating_platform",
  SHOW_SYNC_PROGRESS = "show_sync_progress",
  TASK_ID = "task_id",
  TRACKING_ID = "tracking_id",
  DATA_RANGE_FROM = "data_range_from",
  DATA_RANGE_TO = "data_range_to",
  DATA_RANGE_PRESET = "data_range_preset",
  WIZARD_STEP = "wizard_step",
  INTEGRATION_MODE = "integration_mode",
  EDIT_PROGRESS_STATE = "edit_progress_state",
  DUPLICATE_APPS_LAST_CHECK = "duplicate_apps_last_check",
  DUPLICATE_APPS_USER_DISMISSED = "duplicate_apps_user_dismissed",
}

/**
 * How many times per day the platform checks for duplicate AdMob apps.
 * Interval = 24h / DUPLICATE_CHECK_DAILY_COUNT.
 * Set to 1 → once per day, 4 → every 6 hours, etc.
 */
export const DUPLICATE_CHECK_DAILY_COUNT = 4;

export enum GOOGLE_CAMPAIGN_STATUS {
  UNSPECIFIED = "0",
  UNKNOWN = "1",
  ENABLED = "2",
  PAUSED = "3",
  REMOVED = "4",
}

export enum CAMPAIGN_STATUS {
  UNSPECIFIED = "Unspecified",
  UNKNOWN = "Unknown",
  ENABLED = "Enabled",
  PAUSED = "Paused",
  REMOVED = "Removed",
}


export enum META_CAMPAIGN_STATUS {
  PAUSED = "PAUSED",
  ACTIVE = "ACTIVE",
}

export enum APPLE_CAMPAIGN_STATUS {
  ENABLED = "ENABLED",
  PAUSED = "PAUSED",
}

export const STATUS_OPTIONS: StatusOptionData[] = [
  { label: "All Status", value: "all", color: "gray" },
  { label: "Growth (TN/TR > 0.5)", value: "success", color: "green" },
  { label: "Stable (0 ≤ TN/TR ≤ 0.5)", value: "warning", color: "yellow" },
  { label: "Declining (TN/TR < 0)", value: "danger", color: "red" },
];

export enum DATA_SYNCING_WEBSOCKET_STATUS {
  COMPLETED = "completed",
  IN_PROGRESS = "in_progress",
  ERROR = "error",
}

export const GOOGLE_GUIDE_STEPS_IMAGES_MAPPER = {
  "1": {
    fileName: "1 Google_IAP_GCP_Project_Select.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/1 Google_IAP_GCP_Project_Select.png",
  },
  "2": {
    fileName: "2 Google_IAP_GCP_Enable_Androidpublisher_API.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/2 Google_IAP_GCP_Enable_Androidpublisher_API.png",
  },
  "3": {
    fileName: "3 Google_IAP_GCP_Enable_DeveloperReporting_API.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/3 Google_IAP_GCP_Enable_DeveloperReporting_API.png",
  },
  "4": {
    fileName: "4 Google_IAP_GCP_Enable_CloudPubSub_API.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/4 Google_IAP_GCP_Enable_CloudPubSub_API.png",
  },
  "5": {
    fileName: "5 Google_IAP_GCP_API_Enable_Verified.jpg",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/5 Google_IAP_GCP_API_Enable_Verified.png",
  },
  "6": {
    fileName: "6 Google_IAP_GCP_CreateServiceAccountMain.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/6 Google_IAP_GCP_CreateServiceAccountMain.png",
  },
  "7": {
    fileName: "7 Google_IAP_GCP_SA_1.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/7 Google_IAP_GCP_SA_1.png",
  },
  "8": {
    fileName: "8 Google_IAP_GCP_SA_2.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/8 Google_IAP_GCP_SA_2.png",
  },
  "9": {
    fileName: "9 Google_IAP_GCP_SA_StorageObject.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/9 Google_IAP_GCP_SA_StorageObject.png",
  },
  "10": {
    fileName: "10 Google_IAP_GCP_SA_MonitoringVeiw.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/10 Google_IAP_GCP_SA_MonitoringVeiw.png",
  },
  "11": {
    fileName: "11 Google_IAP_GCP_SA_PubSubAdmin.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/11 Google_IAP_GCP_SA_PubSubAdmin.png",
  },
  "12": {
    fileName: "12 Google_IAP_GCP_SA_PermissionsSummary.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/12 Google_IAP_GCP_SA_PermissionsSummary.png",
  },
  "13": {
    fileName: "13 Google_IAP_GCP_SA_CreatedSummary.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/13 Google_IAP_GCP_SA_CreatedSummary.png",
  },
  "14": {
    fileName: "14 Google_IAP_PlayConsole_GrantPermission_To_SA.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/14 Google_IAP_PlayConsole_GrantPermission_To_SA.png",
  },
  "15": {
    fileName: "15 Google_IAP_PlayConsole_GrantPermission_To_SA_Invite.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/15 Google_IAP_PlayConsole_GrantPermission_To_SA_Invite.png",
  },
  "15-2": {
    fileName: "15 Google_IAP_PlayConsole_GrantPermission_To_SA_Invite.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/15 Google_IAP_PlayConsole_GrantPermission_To_SA_Permission1.png",
  },
  "16": {
    fileName: "16 Google_IAP_PlayConsole_GrantPermission_To_SA_Permission2.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/16 Google_IAP_PlayConsole_GrantPermission_To_SA_Permission2.png",
  },
  "17": {
    fileName: "17 Google_IAP_GCP_SA_ManageKeys.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/17 Google_IAP_GCP_SA_ManageKeys.png",
  },
  "18": {
    fileName: "18 Google_IAP_GCP_SA_KeysJSON.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/18 Google_IAP_GCP_SA_KeysJSON.png",
  },
  "19": {
    fileName: "19 Google_IAP_GCP_SA_KeysJSONFinal.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/19 Google_IAP_GCP_SA_KeysJSONFinal.png",
  },
  "20": {
    fileName: "20 Google_IAP_Copy_ServiceAccount_Email.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/20 Google_IAP_Copy_ServiceAccount_Email.png",
  },
  "21": {
    fileName: "21 GoogleIAP_Finance_Reports_Cloud_URI_Image.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/21 GoogleIAP_Finance_Reports_Cloud_URI_Image.png",
  },
  "22": {
    fileName: "22 AppRevBooster_GoogleIAP_Integration.png",
    link: "https://assets.apprevbooster.com/googleIAPSupportGuideImages/22 AppRevBooster_GoogleIAP_Integration.png",
  },
};

export const APPLE_GUIDE_STEPS_IMAGES_MAPPER = {
  "1": {
    fileName: "1_app-store-connect-api.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/1_app-store-connect-api.png",
  },
  "2": {
    fileName: "2_generate-api-key.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/2_generate-api-key.png",
  },
  "3": {
    fileName: "3 Apple_IAP_AppStoreConnect_Select.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/3_download_keyfile.png",
  },
  "4": {
    fileName: "4_connect-api-key.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/4_connect-api-key.png",
  },
  "5": {
    fileName: "5_issuer-id.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/5_issuer-id.png",
  },
  "6": {
    fileName: "6_key-id.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/6_key-id.png",
  },
  "7": {
    fileName: "7_Vendor_No_ViewPaymentsandProceeds-1_2x-2.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/7_Vendor_No_ViewPaymentsandProceeds-1_2x-2.png",
  },
  "8": {
    fileName: "8 Apple_IAP_Integration_ModalPopup.png",
    link: "https://assets.apprevbooster.com/appleIAPSupportGuideImages/8_Apple_IAP_Integration_ModalPopup.png",
  },
};

export const APPLE_SEARCH_ADS_GUIDE_STEPS_IMAGES_MAPPER = {
  "1": {
    fileName: "1_apple_search_ads_download_public_key.png",
    link: "https://assets.apprevbooster.com/appleSearchAdsSupportGuideImages/1_download-publick-key.png",
  },
  "2": {
    fileName: "2_apple_search_ads_invite_user.png",
    link: "https://assets.apprevbooster.com/appleSearchAdsSupportGuideImages/2_create_ids_using_downloaded_public_key.png",
  },
  "3": {
    fileName: "3_apple_search_ads_api_account_manager_role.png",
    link: "https://assets.apprevbooster.com/appleSearchAdsSupportGuideImages/3_add_generated_ids_and_connect.png",
  }
};

export const SPENDING_PLATFORMS: PlatformConfig[] = [
  {
    id: "google_ads",
    name: "Google Ads",
    icon: APP_PLATFORM_LOGO_MAP.google_ads,
    type: "spending",
    authType: "oauth",
    description: "configurations.integrations.google_ads_desc",
  },
  {
    id: "apple_search_ads",
    name: "Apple Search Ads",
    icon: APP_PLATFORM_LOGO_MAP.app_store,
    type: "spending",
    authType: "form",
    description: "configurations.integrations.apple_search_ads_desc",
  },
  {
    id: "meta_ads",
    name: "Meta Ads",
    icon: APP_PLATFORM_LOGO_MAP.meta,
    type: "spending",
    authType: "oauth",
    description: "configurations.integrations.meta_ads_desc",
    comingSoon: true
  }
];

export const REVENUE_PLATFORMS: PlatformConfig[] = [
  {
    id: "google_admob",
    name: "Google AdMob",
    icon: APP_PLATFORM_LOGO_MAP.google_admob,
    type: "revenue",
    authType: "oauth",
    description: "configurations.integrations.google_admob_desc",
  },
  {
    id: "apple_iap",
    name: "Apple IAP",
    icon: APP_PLATFORM_LOGO_MAP.apple,
    type: "revenue",
    authType: "form",
    description: "configurations.integrations.apple_iap_desc",
  },
  {
    id: "google_iap",
    name: "Google Play IAP",
    icon: APP_PLATFORM_LOGO_MAP.playStore,
    type: "revenue",
    authType: "form",
    description: "configurations.integrations.google_iap_desc",
  },
];

export const DEMO_ACCOUNTS = ["demo1", "demo2"]

export enum SupportTicketsEnum {
  GENERAL = "general",
  TECHNICAL = "technical",
  FEATURE_REQUEST = "feature_request",
  BUG_REPORT = "bug_report",
  ACCOUNT = "account",
  OTHER = "other"
}

export const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
  path: {
    login: "login",
    viewAs: "admin/view-as",
    logout: "users/logout",
    register: "register",
    users: "users",
    refreshToken: "token/refresh",
    me: "users/me",
    userActivity: "user-activity",
    permissions: "permissions",
    categorizedPermissions: "permissions/category",
    onboarding: "onboarding",
    oauthCallback: "oauth/callback",
    teamMembers: "team-members",
    teamMembersSessions: "team-members/sessions",
    acceptInvite: "invite-user-accept",
    changePassword: "change-password",
    forgotPassword: "forgot-password",
    resetPassword: "password-reset-confirm",
    insertApps: "insert-apps",
    getApps: "apps",
    roles: "roles",
    getAppDetails: "apps/details",
    getAppName: "apps/get-app-name",
    getAppsChart: "apps/chart",
    getAppsCountryChart: "apps/country-chart",
    dashboard: "apps/dashboard",
    campaigns: "campaigns",
    getAppBidManagement: "apps/get-campaign-bid-data",
    getCampaignHistory: "get-campaign-history",
    updateCampaignBid: "update-campaign-bid",
    updateCampaignBudget: "campaigns/budget",
    campaignsStatus: "campaigns/status",
    getCampaignCountryCost: "get-campaign-country-cost",
    googleOAuth: "google-auth",
    googleOAuthCallback: "google-auth-callback",
    connectedAccounts: "connected-accounts",
    userDetailsFromEmail: "users/email",
    syncCampaigns: "campaigns/sync",
    syncApps: "apps/sync",
    disconnectAccount: "disconnect-account",
    countryReports: "apps/country-reports",
    // Integration
    trackingIntegration: "tracking-integration",
    socialAccountStatus: "tracking-integration/social-account-status",
    latestSocialAccountStatus: "tracking-integration/latest-social-account-status",
    sendIncompleteIntegrationReminder: "tracking-integration/send-incomplete-reminder",
    sendIntegrationCompletionConfirmation: "tracking-integration/send-completion-confirmation",
    googleAdsAccounts: "google-ads-accounts",
    googleAdsSubAccounts: "get-sub-account-list",
    googleAdsSaveAccount: "google-ads-save-account",
    deleteSocialAccount: "social-account",
    appleAdsAccounts: "insert-apple-iap",
    getGrowthIndicator: "get-growth-indicator",
    updateGrowthIndicator: "update-growth-indicator",
    googleIapInsert: "connect-google-iap",
    appleIapSelectApps: "apple-iap/select-apps",
    googleIapSelectApps: "google-iap/select-apps",
    integrationApps: "integrations/apps",
    integrationAppsSelection: "integrations/apps/selection",
    supportTickets: "support-tickets",
    googleIapOrders: "google-iap/orders",
    googleIapAppSummary: "google-iap/apps/summary",
    googleIapAppOrders: "google-iap/apps/:slug/orders",
    googleIapAppOrdersConsolidated: "google-iap/apps/order",
    // Meta Ads
    metaAdsAuth: "meta-ads/auth",
    metaAdsCallback: "meta-ads/auth/callback",
    metaAdsAccounts: "meta-ads/accounts",
    metaAdsSaveAccount: "meta-ads/accounts/save",
    metaAdsCampaigns: "meta-ads/campaigns",
    metaAdsCampaignDetail: "meta-ads/campaigns/:id",
    metaAdsCampaignDetailUpdate: "meta-ads/campaigns/:id/update",
    metaAdsTaskProgress: "meta-ads/tasks/:taskId/progress",
    // Apple Search Ads
    connectAppleAds: "apple-ads",
    appleAdsSpending: "apple-ads",
    activateAppleAds: "activate-apple-ads",
    googleAdsCampaignList: "/google-ads/campaigns",
    googleAdsCampaignDetail: "/google-ads/campaigns/:id",
    appleAdsCampaignList: "/apple-ads/campaigns",
    appleAdsCampaigns: "apple-ads/campaigns",
    appleAdsCampaignDetail: "apple-ads/campaigns/:id",
    appleAdsCampaignUpdate: "apple-ads/campaigns/:id/update",
    appleAdsBudgetHistory: "apple-ads/campaigns/budget-history",
    appleAdsTaskProgress: "apple-ads/task-progress",
    // Duplicate AdMob apps
    duplicateApps: "apps/duplicates",
    linkApps: "apps/link",
    // AI Assistant
    agentInit: "agent/init",
    // Pricing
    stripeCheckout: "stripe/checkout",
    stripePlans: "stripe/plans",
    razorpayVerify: "razorpay/verify",
    analyticsTrack: "analytics/track",
    stripeHistory: "stripe/subscription-history",
    stripeDetails: "stripe/subscription-details",
    stripeTeamOverview: "stripe/team-overview",
    stripeCancel: "stripe/cancel-subscription",
    stripeChangePlan: "stripe/change-plan",
    stripeConfirmPlanChange: "stripe/confirm-plan-change",
    stripeCancelPendingChange: "stripe/cancel-pending-plan-change",
    stripeReactivate: "stripe/reactivate-subscription",
    billingAddons: "billing/addons",
    stripeAddonPurchase: "stripe/addon-purchase",
    billingOpenInvoice: "billing/open-invoice",
    stripeActivateTrial: "stripe/activate-trial",
    // Referral
    referralValidate: "referral/validate",
    // Reports
    campaignProfitability: "reports/campaign-profitability",
    countryProfitability: "reports/country-profitability",
    appProfitLeaderboard: "reports/app-profit-leaderboard",
    osProfitability: "reports/os-profitability",
    adPlatformProfitability: "reports/ad-platform-profitability",
    breakEvenRoas: "reports/break-even-roas",
    spendEfficiency: "reports/spend-efficiency",
    revenueConcentration: "reports/revenue-concentration",
    scalingOpportunity: "reports/scaling-opportunity",
    monthlySummary: "reports/monthly-summary",
    adFormatPerformance: "reports/ad-format-performance",
    adNetworkRevenueShare: "reports/ad-network-revenue-share",
    fillRate: "reports/fill-rate",
    ecpmQuality: "reports/ecpm-quality",
    mediationPerformance: "reports/mediation-performance",
    ecpmComparison: "reports/ecpm-comparison",
    creativePerformance: "reports/creative-performance",
    campaignKillList: "reports/campaign-kill-list",
    bidImpact: "reports/bid-impact",
    countryExpansion: "reports/country-expansion",
    appPortfolioHealth: "reports/app-portfolio-health",
    revenueAnomaly: "reports/revenue-anomaly",
    revenueReconciliation: "reports/revenue-reconciliation",
    // AI Reports
    aiBusinessPerformance: "reports/ai/business-performance",
    aiMarketingAnalysis: "reports/ai/marketing-analysis",
    aiMonetizationReport: "reports/ai/monetization",
    aiGrowthOpportunity: "reports/ai/growth-opportunity",
    aiRiskStability: "reports/ai/risk-stability",
  },
};

export const REPORT_PATHS = {
  // GROUP 1 — Profitability
  R01_CAMPAIGN_PROFITABILITY: "reports/campaign-profitability",
  R02_COUNTRY_PROFITABILITY: "reports/country-profitability",
  R03_APP_PROFIT_LEADERBOARD: "reports/app-profit-leaderboard",
  R04_OS_PROFITABILITY: "reports/os-profitability",
  R05_AD_PLATFORM_PROFITABILITY: "reports/ad-platform-profitability",
  R06_BREAKEVEN_ROAS: "reports/break-even-roas",

  // GROUP 2 — Efficiency
  R07_SPEND_EFFICIENCY: "reports/spend-efficiency",
  R08_REVENUE_CONCENTRATION: "reports/revenue-concentration",
  R19_BID_IMPACT_ANALYSIS: "reports/bid-impact",

  // GROUP 3 — Scaling
  R09_SCALING_OPPORTUNITY: "reports/scaling-opportunity",
  R20_COUNTRY_EXPANSION: "reports/country-expansion",

  // GROUP 4 — Overview
  R10_MONTHLY_SUMMARY: "reports/monthly-summary",

  // GROUP 5 — Monetization
  R11_AD_FORMAT_PERFORMANCE: "reports/ad-format-performance",
  R12_AD_NETWORK_SHARE: "reports/ad-network-revenue-share",
  R13_FILL_RATE_REPORT: "reports/fill-rate",
  R14_AD_REVENUE_QUALITY: "reports/ecpm-quality",
  R15_MEDIATION_PERFORMANCE: "reports/mediation-performance",
  R16_ECPM_COMPARISON: "reports/ecpm-comparison",

  // GROUP 6 — Campaign Intelligence
  R17_CREATIVE_PERFORMANCE: "reports/creative-performance",
  R18_CAMPAIGN_KILL_LIST: "reports/campaign-kill-list",

  // GROUP 7 — Portfolio & Risk
  R21_APP_PORTFOLIO_HEALTH: "reports/app-portfolio-health",
  R22_REVENUE_ANOMALY: "reports/revenue-anomaly",
  R23_REVENUE_RECONCILIATION: "reports/revenue-reconciliation",

  // GROUP 8 — AI Intelligence
  AI01_BUSINESS_PERFORMANCE: "reports/ai/business-performance",
  AI02_PROFIT_FORECASTER: "reports/ai/profit-forecast",
  AI03_BUDGET_OPTIMIZER: "reports/ai/budget-optimization",
  AI04_CHURN_PREDICTOR: "reports/ai/churn-prediction",
  AI05_SEGMENT_INSIGHTS: "reports/ai/segment-insights"
};
